// Relative URL goes through Vite's dev-server proxy (vite.config.ts forwards
// `/api/v1/*` → backend `/api/*`). Using a relative base means the request is
// issued against the same origin as the page, so we don't depend on the user's
// machine being able to reach the backend port directly (VSCode only forwards
// the frontend port, not the backend port).
const API_BASE_URL = '/api/v1';

interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
}

export class ApiClient {
  private static mode: Promise<{ enabled: boolean; csrf_token?: string }> | null = null;

  static authenticationMode() {
    this.mode ??= fetch(`${API_BASE_URL}/auth/sso/status`, { credentials: 'same-origin', cache: 'no-store' })
      .then(async (response) => response.ok ? response.json() : { enabled: false })
      .catch(() => ({ enabled: false }));
    return this.mode;
  }

  static async authHeaders(): Promise<Record<string, string>> {
    const mode = await this.authenticationMode();
    if (mode?.enabled) return mode.csrf_token ? { 'X-CSRF-TOKEN': mode.csrf_token } : {};
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private static getToken(): string | null {
    try {
      const stored = localStorage.getItem('auth-storage');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.state?.token ?? null;
      }
    } catch {
      // ignore parse errors
    }
    return null;
  }

  static async request<T>(
    endpoint: string,
    options: RequestInit = {},
    redirectOnUnauthorized = true
  ): Promise<T> {
    const authHeaders = await this.authHeaders();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...authHeaders,
        ...options.headers,
      },
    });

    if (response.status === 401) {
      // Token expired or invalid — clear auth state
      if (!(await this.authenticationMode())?.enabled) localStorage.removeItem('auth-storage');
      if (redirectOnUnauthorized) window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        message: `HTTP ${response.status}`,
      }));
      throw error;
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  static get<T>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  static post<T>(endpoint: string, data?: unknown) {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  static put<T>(endpoint: string, data?: unknown) {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  static delete<T>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  static async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const authHeaders = await this.authHeaders();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...authHeaders,
      },
      body: formData,
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        message: `HTTP ${response.status}`,
      }));
      throw error;
    }

    return response.json();
  }
}
